import {assertNever} from '@Utils/assert-never.util';
import {MERGE_TAGS} from '@Libraries/email-maker-merge-tags';
import type {MergeTag} from '@Components/merge-tags/merge-tags.types';
import {capitalizeFirstLetter, lowerCaseFirstLetter} from '@Utils/string.util';

export const FIRST_NAME_MERGE_TAG_KEY = 'firstName';
export const LAST_NAME_MERGE_TAG_KEY = 'lastName';
export const PHONE_NUMBER_MERGE_TAG_KEY = 'phoneNumber';
export const BIRTHDAY_MERGE_TAG_KEY = 'birthday';
export const ADDRESS_MERGE_TAG_KEY = 'address';

export const MERGE_TAG_TOKEN_PATTERN = String.raw`\[\[(\w+)(?:::(.*?))?\]\]`;
const EMAIL_MERGE_TAG_NAME = 'Email';
export const ANY_TOKEN_PATTERN = String.raw`\[\[\w+(?:::.*?)?\]\]`;
const PREPOSITIONS_BEFORE_TOKEN = ['on', 'at', 'for', 'to', 'in', 'from', 'with', 'by'];
const LINK_PATTERN = /(?:https?:\/\/|www\.)\S+/gi;
const MARKUP_PATTERN = /<(?!\s)(?:"[^"]*(?:"|$)|'[^']*(?:'|$)|[^>"'])*(?:>|$)/g;
export const WHITESPACE_RUN_PATTERN = /\s+/g;
const ENGLISH_GREETING_FALLBACK_NAME = 'there';
const GREETING_WITH_NAME_PATTERN = /\b(Hi|Hey|Hello|Dear)\s+(?:\[\[FirstName(?:::.*?)?\]\](?:\s+\[\[LastName(?:::.*?)?\]\])?|\[\[LastName(?:::.*?)?\]\])/gi;
const POSSESSIVE_OF_TOKEN_PATTERN = new RegExp(String.raw`${ANY_TOKEN_PATTERN}['’]s\s*`, 'g');
const TOKEN_BETWEEN_COMMAS_PATTERN = new RegExp(String.raw`\s*,\s*${ANY_TOKEN_PATTERN}\s*,`, 'g');
const COMMA_BEFORE_TOKEN_PATTERN = new RegExp(String.raw`\s*,\s*${ANY_TOKEN_PATTERN}`, 'g');
const TOKEN_WITH_SEPARATOR_PATTERN = new RegExp(String.raw`\s*${ANY_TOKEN_PATTERN}\s*[,!:]?\s*`, 'g');
const SPACE_BEFORE_PUNCTUATION_PATTERN = /\s+([.,!?:])/g;
const REPEATED_SPACE_PATTERN = /\s{2,}/g;
const TRAILING_LINE_BREAKS_PATTERN = /[\r\n]+$/;
const LINE_BREAKS_PATTERN = /[\r\n]+/g;
const LEADING_PUNCTUATION_PATTERN = /^[,!:.\-–—]\s*/;
const TRAILING_SEPARATOR_PATTERN = /\s*[,:;–—]$/;
const MAX_CONTACT_VALUE_LENGTH = 40;
const EMPTY_CONTACT_VALUE = '0';
const CHIP_PLACEHOLDER_CHARACTER = 'x';
const SENTENCE_CASE_LABEL_KEYS: Record<string, string> = {
  firstName: 'pmwjs_merge_tag_first_name',
  lastName: 'pmwjs_merge_tag_last_name',
};
const LABELLED_TAG_START = '{';
const LABELLED_TAG_END = '}';
const NAME_MERGE_TAG_KEYS: readonly string[] = [FIRST_NAME_MERGE_TAG_KEY, LAST_NAME_MERGE_TAG_KEY];
const MISSING_FIELDS_SEPARATOR = ' / ';
const CUSTOM_FIELD_MERGE_TAG_ICON = 'icon-tags';
const MAX_INLINE_FIELD_LABEL_LENGTH = 24;

export const EMAIL_MERGE_TAG_KEY = lowerCaseFirstLetter(EMAIL_MERGE_TAG_NAME);
export const CHIP_LENGTH_ALLOWANCE = 10;

export enum FallbackAudience {
  NO_NAME = 'no-name',
  NO_LAST_NAME = 'no-last-name',
  NO_FULL_NAME = 'no-full-name',
  MISSING_FIELDS = 'missing-fields',
}

export interface ResolvedLine {
  text: string;
  isFallback: boolean;
  isFallbackMissing: boolean;
}

const toCaseInsensitivePattern = (word: string): string => {
  return word
    .split('')
    .map((character: string): string => `[${character.toLowerCase()}${character.toUpperCase()}]`)
    .join('');
};

const PREPOSITION_BEFORE_TOKEN_PATTERN = new RegExp(
  String.raw`\b(?:${PREPOSITIONS_BEFORE_TOKEN.map(toCaseInsensitivePattern).join('|')})\s+${ANY_TOKEN_PATTERN}`,
  'g'
);

const getMergeTagNamesInLine = (line: string): string[] => {
  return [...line.matchAll(new RegExp(MERGE_TAG_TOKEN_PATTERN, 'g'))].map((match: RegExpMatchArray): string => match[1]);
};

const toUniqueLowerCamelKeys = (tagNames: string[]): string[] => {
  return [...new Set(tagNames.map((tagName: string): string => lowerCaseFirstLetter(tagName)))];
};

export const getMergeTagLabels = (mergeTags: MergeTag[]): Record<string, string> => {
  return Object.fromEntries(mergeTags.map((tag): [string, string] => [tag.mergeTagKey, window.i18next.t(SENTENCE_CASE_LABEL_KEYS[tag.mergeTagKey] ?? tag.label)]));
};

/** Names each tag by its label, e.g. {First name}, so the owner can read a line outside the wizard; custom fields show their tag name. */
export const getLabelledLine = (line: string): string => {
  const labelsByMergeTagKey = getMergeTagLabels(MERGE_TAGS);
  return line.replace(new RegExp(MERGE_TAG_TOKEN_PATTERN, 'g'), (_token: string, tagName: string): string => {
    return `${LABELLED_TAG_START}${getMergeTagLabel(lowerCaseFirstLetter(tagName), labelsByMergeTagKey)}${LABELLED_TAG_END}`;
  });
};

/** Falls back to the token name while a custom field's label is loading or gone. */
export const getMergeTagLabel = (mergeTagKey: string, labelsByMergeTagKey: Record<string, string>): string => {
  return Object.hasOwn(labelsByMergeTagKey, mergeTagKey) ? labelsByMergeTagKey[mergeTagKey] : capitalizeFirstLetter(mergeTagKey);
};

export const getMergeTagIcon = (mergeTagKey: string): string => {
  return MERGE_TAGS.find((mergeTag): boolean => mergeTag.mergeTagKey === mergeTagKey)?.icon ?? CUSTOM_FIELD_MERGE_TAG_ICON;
};

export const toMergeTagToken = (mergeTagKey: string): string => {
  return `[[${capitalizeFirstLetter(mergeTagKey)}]]`;
};

export const getMergeTagKeysInLine = (line: string): string[] => {
  return toUniqueLowerCamelKeys(getMergeTagNamesInLine(line));
};

/** Every tag except one named exactly `Email`, which never needs a fallback. */
export const getGapMergeTagKeys = (line: string): string[] => {
  return toUniqueLowerCamelKeys(getMergeTagNamesInLine(line).filter((tagName: string): boolean => tagName !== EMAIL_MERGE_TAG_NAME));
};

/** Subjects and preview text are one line: a trailing line break is dropped and any other becomes a space. */
export const flattenLineBreaks = (text: string): string => {
  return text.replace(TRAILING_LINE_BREAKS_PATTERN, '').replace(LINE_BREAKS_PATTERN, ' ');
};

export const hasMergeTagToken = (text: string): boolean => {
  return new RegExp(MERGE_TAG_TOKEN_PATTERN).test(text);
};

/** A line without gap tags saves no fallback, not even one kept for re-adding a chip. */
export const getFallbackToSave = (line: string, fallback: string): string => {
  return getGapMergeTagKeys(line).length > 0 ? fallback : '';
};

export const isLinePersonalized = (line: string, fallback: string): boolean => {
  return getMergeTagKeysInLine(line).length > 0 && (getGapMergeTagKeys(line).length === 0 || fallback.trim() !== '');
};

const capToContactValueLength = (value: string): string => {
  return Array.from(value).slice(0, MAX_CONTACT_VALUE_LENGTH).join('');
};

const toSingleLine = (value: string): string => {
  return value.replace(WHITESPACE_RUN_PATTERN, ' ').trim();
};

const cleanTextValue = (value: string): string => {
  const cleanedValue = toSingleLine(value.replace(MARKUP_PATTERN, '').replace(LINK_PATTERN, ''));
  return cleanedValue === EMPTY_CONTACT_VALUE ? '' : capToContactValueLength(cleanedValue).trimEnd();
};

const cleanEmailValue = (value: string): string => {
  const cleanedValue = toSingleLine(value.replace(MARKUP_PATTERN, ''));
  return cleanedValue === EMPTY_CONTACT_VALUE ? '' : cleanedValue;
};

export const cleanContactValue = (tagName: string, value: string): string => {
  return tagName === EMAIL_MERGE_TAG_NAME ? cleanEmailValue(value) : cleanTextValue(value);
};

const hasContactValue = (valuesByKey: Record<string, string>, key: string): boolean => {
  return cleanTextValue(valuesByKey[key] ?? '') !== '';
};

/** A contact who needs the fallback before it is written is flagged with isFallbackMissing, not given an empty line. */
export const resolvePersonalizedLine = (line: string, fallback: string, valuesByKey: Record<string, string>): ResolvedLine => {
  const hasEveryField = getGapMergeTagKeys(line).every((key: string): boolean => hasContactValue(valuesByKey, key));
  if (!hasEveryField) {
    return {text: fallback, isFallback: true, isFallbackMissing: fallback.trim() === ''};
  }

  const text = line.replace(new RegExp(MERGE_TAG_TOKEN_PATTERN, 'g'), (_token: string, tagName: string): string =>
    cleanContactValue(tagName, valuesByKey[lowerCaseFirstLetter(tagName)] ?? '')
  );
  return {text, isFallback: false, isFallbackMissing: false};
};

const dropMergeTokens = (line: string): string => {
  return line
    .replace(TOKEN_BETWEEN_COMMAS_PATTERN, '')
    .replace(COMMA_BEFORE_TOKEN_PATTERN, '')
    .replace(POSSESSIVE_OF_TOKEN_PATTERN, '')
    .replace(PREPOSITION_BEFORE_TOKEN_PATTERN, '')
    .replace(TOKEN_WITH_SEPARATOR_PATTERN, ' ');
};

const tidyPunctuationAndSpacing = (line: string): string => {
  return line
    .replace(SPACE_BEFORE_PUNCTUATION_PATTERN, '$1')
    .replace(REPEATED_SPACE_PATTERN, ' ')
    .trim()
    .replace(LEADING_PUNCTUATION_PATTERN, '')
    .replace(TRAILING_SEPARATOR_PATTERN, '');
};

const capitalizeSentence = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export const suggestFallbackLine = (line: string): string => {
  const withGreeting = line.replace(GREETING_WITH_NAME_PATTERN, (_match: string, greetingWord: string): string => `${greetingWord} ${ENGLISH_GREETING_FALLBACK_NAME}`);
  return capitalizeSentence(tidyPunctuationAndSpacing(dropMergeTokens(withGreeting)));
};

/** Follows the line while it still equals the suggestion; once edited, the fallback is the user's. */
export const getNextFallback = (previousLine: string, nextLine: string, currentFallback: string): string => {
  const wasSuggested = currentFallback === '' || currentFallback === suggestFallbackLine(previousLine);
  if (!wasSuggested) {
    return currentFallback;
  }

  return getGapMergeTagKeys(nextLine).length > 0 ? suggestFallbackLine(nextLine) : '';
};

export const getPersonalizedLineLength = (line: string): number => {
  return line.replace(new RegExp(MERGE_TAG_TOKEN_PATTERN, 'g'), CHIP_PLACEHOLDER_CHARACTER.repeat(CHIP_LENGTH_ALLOWANCE)).length;
};

export const getFallbackAudience = (gapKeys: string[]): FallbackAudience => {
  if (gapKeys.some((key: string): boolean => !NAME_MERGE_TAG_KEYS.includes(key))) {
    return FallbackAudience.MISSING_FIELDS;
  }
  if (gapKeys.length > 1) {
    return FallbackAudience.NO_FULL_NAME;
  }

  return gapKeys[0] === LAST_NAME_MERGE_TAG_KEY ? FallbackAudience.NO_LAST_NAME : FallbackAudience.NO_NAME;
};

export const getMissingFieldsText = (missingKeys: string[], labelsByMergeTagKey: Record<string, string>): string => {
  return missingKeys.map((key: string): string => getMergeTagLabel(key, labelsByMergeTagKey).toLowerCase()).join(MISSING_FIELDS_SEPARATOR);
};

export const getFallbackAudienceText = (gapKeys: string[], labelsByMergeTagKey: Record<string, string>): string => {
  const audience = getFallbackAudience(gapKeys);
  switch (audience) {
    case FallbackAudience.NO_NAME:
      return window.i18next.t('pmwjs_contacts_without_a_name');
    case FallbackAudience.NO_LAST_NAME:
      return window.i18next.t('pmwjs_contacts_without_a_last_name');
    case FallbackAudience.NO_FULL_NAME:
      return window.i18next.t('pmwjs_contacts_missing_first_or_last_name');
    case FallbackAudience.MISSING_FIELDS:
      return window.i18next.t('pmwjs_contacts_missing_fields', {fields: getMissingFieldsText(gapKeys, labelsByMergeTagKey)});
    default:
      return assertNever(audience);
  }
};

const hasLongFieldLabel = (gapKeys: string[], labelsByMergeTagKey: Record<string, string>): boolean => {
  return gapKeys.some((key: string): boolean => getMergeTagLabel(key, labelsByMergeTagKey).length > MAX_INLINE_FIELD_LABEL_LENGTH);
};

/** One short phrase for a one-line label; the full list goes in a title. */
export const getShortFallbackAudienceText = (gapKeys: string[], labelsByMergeTagKey: Record<string, string>): string => {
  if (getFallbackAudience(gapKeys) === FallbackAudience.MISSING_FIELDS && (gapKeys.length > 1 || hasLongFieldLabel(gapKeys, labelsByMergeTagKey))) {
    return window.i18next.t('pmwjs_contacts_missing_a_field');
  }

  return getFallbackAudienceText(gapKeys, labelsByMergeTagKey);
};

export const isNoNameVersion = (gapKeys: string[]): boolean => {
  return getFallbackAudience(gapKeys) !== FallbackAudience.MISSING_FIELDS;
};
